import { type FunctionComponent } from "react";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";
import { useOutletContext } from "react-router-dom";
import moment from "moment-timezone";

import { Select, TextBox, DateTimeInput } from "components/Shared/Inputs";
import { type IncidentReport } from "components/CriticalIncident/interfaces";
import { useThrottledSectionUpdate } from '../../hooks/useThrottledSectionUpdate';
import { FormControlWrapper } from "components/Shared";
import { REF_TYPES, useGetReferences } from "api/queries/referencesQuery";
import {
  IN_PERSON_COUNSELLING_MODALITY_CODE,
  VIDEO_COUNSELLING_MODALITY_CODE,
  TELE_COUNSELLING_MODALITY_CODE,
} from "constants/modalities";

const ALLOWED_MODALITY_CODES: string[] = [
  IN_PERSON_COUNSELLING_MODALITY_CODE,
  VIDEO_COUNSELLING_MODALITY_CODE,
  TELE_COUNSELLING_MODALITY_CODE,
];

const MAX_CHARS = 2000;

export const InterventionSummary: FunctionComponent = () => {
  const { t, ready: translationsReady } = useTranslation('criticalIncident', { useSuspense: false });
  const { values, handleChange, touched, errors, setFieldValue } = useFormikContext<IncidentReport>();
  const { isReadonly = false } = useOutletContext<{ isReadonly: boolean }>();
  const throttledUpdateReport = useThrottledSectionUpdate('interventionSummary', values.interventionSummary);
  const { data: modalityReferences = [] } = useGetReferences(REF_TYPES.MODALITIES);
  const modalities = modalityReferences.filter(modality => ALLOWED_MODALITY_CODES.includes(modality.value));

  return translationsReady && (
    <div className="intervention-summary">
      <h2>{t('interventionSummary')}</h2>
      <Grid container spacing={2}>
        <Grid item sm={6}>
          <FormControlWrapper
            isReadonly={isReadonly}
            type="date"
            dateFormat="L LT"
            label={t('actual_start_date_and_time')}
            name="interventionSummary.actualStartDate"
            required
          >
            <DateTimeInput
              label={t('actual_start_date_and_time')}
              name="interventionSummary.actualStartDate"
              onChange={(val) => {
                const isoString = val?.toDate().toISOString();
                setFieldValue('interventionSummary.actualStartDate', isoString);
                throttledUpdateReport({ actualStartDate: isoString });
              }}
              maxDateTime={moment(values.interventionSummary?.actualEndDate).isValid() ? moment(values.interventionSummary?.actualEndDate) : undefined}
              value={moment(values.interventionSummary?.actualStartDate).isValid() ? moment(values.interventionSummary?.actualStartDate) : null}
              error={touched.interventionSummary?.actualStartDate && Boolean(errors.interventionSummary?.actualStartDate)}
              helperText={touched.interventionSummary?.actualStartDate ? t(errors.interventionSummary?.actualStartDate!, { dateLabel1: t('actual_start_date_and_time'), dateLabel2: t('actual_end_date_and_time') }) : ''}
              fullWidth
            />
          </FormControlWrapper>
        </Grid>
        <Grid item sm={6}>
          <FormControlWrapper
            isReadonly={isReadonly}
            type="date"
            dateFormat="L LT"
            label={t('actual_end_date_and_time')}
            name="interventionSummary.actualEndDate"
            required
          >
            <DateTimeInput
              label={t('actual_end_date_and_time')}
              name="interventionSummary.actualEndDate"
              onChange={(val) => {
                const isoString = val?.toDate().toISOString();
                setFieldValue('interventionSummary.actualEndDate', isoString);
                throttledUpdateReport({ actualEndDate: isoString });
              }}
              minDateTime={moment(values.interventionSummary?.actualStartDate).isValid() ? moment(values.interventionSummary?.actualStartDate) : undefined}
              referenceDate={moment(values.interventionSummary?.actualStartDate).isValid() ? moment(values.interventionSummary?.actualStartDate) : undefined}
              value={moment(values.interventionSummary?.actualEndDate).isValid() ? moment(values.interventionSummary?.actualEndDate) : null}
              error={touched.interventionSummary?.actualEndDate && Boolean(errors.interventionSummary?.actualEndDate)}
              helperText={touched.interventionSummary?.actualEndDate ? t(errors.interventionSummary?.actualEndDate!, { dateLabel1: t('actual_end_date_and_time'), dateLabel2: t('actual_start_date_and_time') }) : ''}
              fullWidth
            />
          </FormControlWrapper>
        </Grid>
        <Grid item sm={12}>
          <FormControlWrapper
            isReadonly={isReadonly}
            type="select"
            fullWidth
            label={`${t('support_modality')}*`}
            name="interventionSummary.supportModality"
            required
            options={modalities.map(modality => ({ value: modality.value, label: modality.title }))}
            error={touched.interventionSummary?.supportModality && Boolean(errors?.interventionSummary?.supportModality)}
            helperText={touched.interventionSummary?.supportModality && t(errors?.interventionSummary?.supportModality || ' ')}
          >
            <Select
              name="interventionSummary.supportModality"
              value={values.interventionSummary?.supportModality ?? ''}
              onChange={(e) => {
                handleChange(e);
                throttledUpdateReport({ supportModality: e.target.value as string });
              }}
            >
              {modalities.map(modality => (
                <MenuItem value={modality.value} key={modality.value}>{modality.title}</MenuItem>
              ))}
            </Select>
          </FormControlWrapper>
        </Grid>
        <Grid item sm={12}>
          <FormControlWrapper
            isReadonly={isReadonly}
            label={t('incident_description')}
            name="interventionSummary.incidentDescription"
            fullWidth
          >
            <TextBox
              multiline
              minRows={4}
              maxRows={6}
              label={t('incident_description')}
              required
              name="interventionSummary.incidentDescription"
              value={values.interventionSummary?.incidentDescription ?? ''}
              onChange={(e) => {
                handleChange(e);
                throttledUpdateReport({ incidentDescription: e.target.value });
              }}
              slotProps={{
                input: { maxLength: MAX_CHARS }
              }}
              error={touched.interventionSummary?.incidentDescription && Boolean(errors.interventionSummary?.incidentDescription)}
              helperText={t(errors.interventionSummary?.incidentDescription || ' ')}
            />
          </FormControlWrapper>
        </Grid>
      </Grid>
    </div>
  );
};
